import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { localServer } from '../Path/Path';

/**
 * SendToSeshaasaiModal
 * Props:
 *   open           {boolean}
 *   onClose        {function}
 *   skuList        {Array}  - [{ sku, qty, size, item }]
 *   preSelectedPo  {string} - Purchasing Document already chosen on Bulk Printing (Case A)
 *   poOptions      {Array}  - PO list from Site+Supplier for Case B
 *   zplBody        {string} - selected ZPL template body
 *   zplName        {string} - selected ZPL template name
 *   multiCurrency  {boolean}
 *   madeInChina    {boolean}
 *   withPrice      {boolean}
 */
const SendToSeshaasaiModal = ({
  open,
  onClose,
  skuList = [],
  preSelectedPo = '',
  poOptions = [],
  zplBody = '',
  zplName = '',
  multiCurrency = false,
  madeInChina = false,
  withPrice = true,
  vendorCode = '',
  vendorDescription = '',
  Add_Char2 = '',
}) => {
  const [selectedPo, setSelectedPo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Normalize poOptions to plain PO number strings
  const normalizedPoOptions = (Array.isArray(poOptions) ? poOptions : [])
    .map((po) => (typeof po === 'string' ? po : po?.PO_Number || po?.poNumber || ''))
    .filter(Boolean);

  const hasPreSelectedPo = !!(preSelectedPo && String(preSelectedPo).trim());

  useEffect(() => {
    if (!open) return;
    setResult(null);
    if (hasPreSelectedPo) {
      // Case A: PO already selected on Bulk Printing form
      setSelectedPo(String(preSelectedPo).trim());
    } else if (normalizedPoOptions.length === 1) {
      // Case B: only one PO available — auto-select
      setSelectedPo(normalizedPoOptions[0]);
    } else {
      setSelectedPo('');
    }
  }, [open, preSelectedPo, poOptions]);

  if (!open) return null;

  const handleSend = async () => {
    if (!selectedPo) {
      toast.error('Please select or enter a PO Number.');
      return;
    }
    if (!skuList || skuList.length === 0) {
      toast.error('No SKU data available to send.');
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${localServer}rfid/send-to-seshaasai`, {
        poNumber: selectedPo,
        skus: skuList,
        zplBody,
        zplName,
        multiCurrency,
        madeInChina,
        withPrice,
        vendorCode,
        vendorDescription,
        Add_Char2,
      });
      setResult(data);
      toast.success(`Job created: ${data.jobId}`);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message || 'Something went wrong.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setSelectedPo('');
    onClose();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>Send to Seshaasai</h3>

        {/* PO selection */}
        <div style={styles.section}>
          <label style={styles.label}>PO Number</label>

          {hasPreSelectedPo ? (
            <>
              <input
                style={{
                  ...styles.input,
                  background: '#f7f7f7',
                  color: '#1a1a1a',
                  WebkitTextFillColor: '#1a1a1a',
                  opacity: 1,
                  fontWeight: 600,
                }}
                value={selectedPo}
                readOnly
              />
              <p style={styles.hint}>PO taken from Purchasing Document on Bulk Printing.</p>
            </>
          ) : normalizedPoOptions.length > 0 ? (
            <>
              <select
                style={{ ...styles.select, color: '#1a1a1a' }}
                value={selectedPo}
                onChange={(e) => setSelectedPo(e.target.value)}
                disabled={!!result}
              >
                <option value="">-- Select a PO --</option>
                {normalizedPoOptions.map((po) => (
                  <option key={po} value={po}>
                    {po}
                  </option>
                ))}
              </select>
              <p style={styles.hint}>
                {normalizedPoOptions.length} PO{normalizedPoOptions.length > 1 ? 's' : ''} available for this Site / Supplier.
              </p>
            </>
          ) : (
            <>
              <input
                style={{ ...styles.input, color: '#1a1a1a', WebkitTextFillColor: '#1a1a1a' }}
                placeholder="Enter PO number manually"
                value={selectedPo}
                onChange={(e) => setSelectedPo(e.target.value)}
                disabled={!!result}
              />
              <p style={styles.hint}>
                No POs loaded for this Site / Supplier. Enter PO number manually.
              </p>
            </>
          )}
        </div>

        {/* SKU summary — scrollable card */}
        <div style={styles.section}>
          <label style={styles.label}>
            SKU ({skuList.length} item{skuList.length !== 1 ? 's' : ''})
          </label>
          <div style={styles.skuCard}>
            {skuList.map((s, i) => (
              <div key={i} style={styles.skuRow}>
                <span style={styles.skuText}>{s.sku}</span>
                <span style={styles.skuText}>Qty: {s.qty}</span>
                <span style={styles.skuText}>Size: {s.size || '60x40mm'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Success result */}
        {result && (
          <div style={styles.successBox}>
            <p style={{ margin: '0 0 6px', color: '#1a1a1a' }}>
              ✔ Job ID: <strong style={{ color: '#1a1a1a' }}>{result.jobId}</strong>
            </p>
            <p style={{ margin: '0 0 6px', color: '#1a1a1a' }}>
              Status: <strong style={{ color: '#237804' }}>{result.status}</strong>
            </p>
            <p style={{ margin: 0, color: '#1a1a1a' }}>SKU Count: {result.skuCount}</p>
          </div>
        )}

        {/* Actions */}
        <div style={styles.actions}>
          {!result && (
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleSend}
              disabled={sending || !selectedPo}
            >
              {sending ? 'Sending...' : 'Confirm & Send'}
            </button>
          )}
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={handleClose}
            disabled={sending}
          >
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    padding: '16px',
    boxSizing: 'border-box',
  },
  modal: {
    background: '#fff', borderRadius: 10, padding: '28px 24px',
    width: '100%', maxWidth: 520,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    color: '#1a1a1a',
  },
  title: { margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#1a1a1a', flexShrink: 0 },
  section: { marginBottom: 18, flexShrink: 0 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#444', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' },
  select: {
    width: '100%', padding: '10px 12px', fontSize: 14,
    border: '1px solid #d9d9d9', borderRadius: 6, outline: 'none',
    background: '#fff', cursor: 'pointer', color: '#1a1a1a',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  input: {
    width: '100%', padding: '10px 12px', fontSize: 14,
    border: '1px solid #d9d9d9', borderRadius: 6, outline: 'none',
    boxSizing: 'border-box', color: '#1a1a1a', background: '#fff',
  },
  hint: { fontSize: 12, color: '#666', marginTop: 5 },
  skuCard: {
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    background: '#fafafa',
    maxHeight: 260,
    overflowY: 'auto',
    padding: '8px',
    boxSizing: 'border-box',
  },
  skuRow: {
    display: 'flex', flexWrap: 'wrap', gap: 10, padding: '8px 10px',
    background: '#fff', borderRadius: 6, marginBottom: 6, fontSize: 13,
    border: '1px solid #eee',
    wordBreak: 'break-all',
  },
  skuText: { color: '#1a1a1a' },
  successBox: {
    background: '#f0fff4', border: '2px solid #52c41a',
    borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontSize: 14,
    lineHeight: 1.8, color: '#1a1a1a', flexShrink: 0,
  },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap', flexShrink: 0 },
  btn: {
    padding: '9px 22px', borderRadius: 6, fontSize: 14,
    cursor: 'pointer', border: 'none', fontWeight: 600, whiteSpace: 'nowrap',
  },
  btnPrimary: { background: '#1890ff', color: '#fff' },
  btnSecondary: { background: '#f0f0f0', color: '#333' },
};

export default SendToSeshaasaiModal;
